import React from 'react';
import { useIntl } from 'react-intl';
import { Card, Tag } from 'antd';
import { JobUtilizationMetrics, JobContainerMetricKey } from 'types/insights';
import './SparkJobUtilizationGrid.scss';

type Props = {
  arcaneMetrics: JobUtilizationMetrics | undefined;
};

type MetricDescriptor = {
  key: JobContainerMetricKey;
  labelId: string;
  unitHint: 'cores' | 'gib';
};

// Logically group the metrics to feed into separate UI Cards
const DRIVER_METRICS: MetricDescriptor[] = [
  {
    key: 'driver.k8s.container.memory_request',
    labelId: 'arcaneMetrics.metric.memory_request',
    unitHint: 'gib',
  },
  {
    key: 'driver.container.memory.usage',
    labelId: 'arcaneMetrics.metric.memory_usage',
    unitHint: 'gib',
  },
  {
    key: 'driver.k8s.container.cpu_request',
    labelId: 'arcaneMetrics.metric.cpu_request',
    unitHint: 'cores',
  },
  {
    key: 'driver.container.cpu.usage',
    labelId: 'arcaneMetrics.metric.cpu_usage',
    unitHint: 'cores',
  },
];

const EXECUTOR_METRICS: MetricDescriptor[] = [
  {
    key: 'executor.k8s.container.memory_request',
    labelId: 'arcaneMetrics.metric.memory_request',
    unitHint: 'gib',
  },
  {
    key: 'executor.container.memory.usage',
    labelId: 'arcaneMetrics.metric.memory_usage',
    unitHint: 'gib',
  },
  {
    key: 'executor.k8s.container.cpu_request',
    labelId: 'arcaneMetrics.metric.cpu_request',
    unitHint: 'cores',
  },
  {
    key: 'executor.container.cpu.usage',
    labelId: 'arcaneMetrics.metric.cpu_usage',
    unitHint: 'cores',
  },
];

const BYTES_PER_GIB = 1024 ** 3;

function formatValue(
  value: number | undefined,
  unitHint: MetricDescriptor['unitHint']
): string {
  if (value === undefined || !Number.isFinite(value)) return '—';

  if (unitHint === 'gib') {
    // Safely assume values > 1000 are raw bytes, otherwise they are already in GiB
    const gibValue = value > 1000 ? value / BYTES_PER_GIB : value;
    return `${gibValue.toFixed(2)} GiB`;
  }

  return `${value.toFixed(3)} cores`;
}

export function SparkJobUtilizationGrid({ arcaneMetrics }: Props) {
  const intl = useIntl();

  if (!arcaneMetrics || Object.keys(arcaneMetrics).length === 0) {
    return (
      <p className="spark-job-utilization__unavailable">
        {intl.formatMessage({ id: 'arcaneMetrics.unavailable' })}
      </p>
    );
  }

  const renderMetricGrid = (descriptors: MetricDescriptor[]) => (
    <ul className="spark-job-utilization__grid">
      {descriptors.map(({ key, labelId, unitHint }) => (
        <li className="spark-job-utilization__tile" key={key}>
          <span className="spark-job-utilization__tile-label">
            {intl.formatMessage({ id: labelId })}
          </span>
          <span className="spark-job-utilization__tile-value">
            {formatValue(arcaneMetrics[key], unitHint)}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="spark-job-utilization">
      <Card
        size="small"
        className="spark-job-utilization__card"
        title="Driver Node"
        extra={<Tag color="blue">Orchestrator</Tag>}>
        {renderMetricGrid(DRIVER_METRICS)}
      </Card>

      <Card
        size="small"
        className="spark-job-utilization__card"
        title="Executor Pods (AVG)"
        extra={<Tag color="purple">Workers</Tag>}>
        {renderMetricGrid(EXECUTOR_METRICS)}
      </Card>
    </div>
  );
}

export default SparkJobUtilizationGrid;
